import {
  type GridCallbackDetails,
  type GridColDef,
  type GridColumnResizeParams,
  type GridFeatureMode,
  gridFilteredSortedRowIdsSelector,
  type GridFilterModel,
  type GridInitialState,
  type GridPinnedColumnFields,
  type GridRowId,
  type GridRowParams,
  type GridRowSelectionModel,
} from '@mui/x-data-grid';
import {
  DataGridPro,
  type DataGridProProps,
  type GridState,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ComponentTestIds } from '~/constants/test-ids';

import LocalStorageService from '~/services/localStorage.service';

import { clone, sortByKeyValues, unique } from '@vestigas/utils';
import DatagridUtils from '~/utils/datagridUtils';
import { LOG, log } from '~/utils/logging';
import {
  usePersistedUserSettings,
  type UserSettingsKeyValues,
} from '~/utils/usePersistedUserSettings';

import {
  Footer,
  LoadingState,
  Pagination,
} from '~/ui/molecules/Datagrid/components';

import { type CustomToolbarButton, GridToolbar } from './GridToolbar';
import { NoRowsOverlay } from './NoRowsOverlay';
import {
  type ColumnOrderModel,
  type ColumnPreset,
  type ColumnVisibilityModel,
  type ColumnWidthModel,
} from './types';
import {
  applyColumnPreset,
  getShouldApplyColumnPreset,
  orderColumns,
  sizeColumns,
} from './utils';

type P = DataGridProProps & {
  readonly checkboxSelection?: boolean;
  readonly columns: GridColDef[];
  /**
   * One-time column layout for this table, applied on the first initialization
   * that sees it. Because initialization happens once, a preset that is
   * `undefined` on the first render and supplied later is never applied — so
   * whatever decides the preset (usually the user's company account) has to be
   * resolved before the table mounts. For the delivery list that is guaranteed
   * by the route's `beforeLoad`, which awaits the user-data query.
   */
  readonly columnPreset?: ColumnPreset;
  readonly defaultHiddenColumns?: string[];
  readonly defaultPinnedColumns?: string;
  readonly defaultRowHeight?: number;
  readonly disableColumnFilter?: boolean;
  readonly disableColumnReorder?: boolean;
  readonly excelColumns?: GridColDef[];
  readonly excelData?: any[];
  readonly filterMode?: string;
  readonly filterModel?: GridFilterModel;
  readonly headerSelectionCheckboxProps?: { checked: boolean };
  readonly hideFooter?: boolean;
  readonly hideHeader?: boolean;
  readonly hideToolbar?: boolean;
  readonly initialState?: GridInitialState;
  readonly internalExportEntityType?: string;
  readonly isError?: boolean;
  readonly isFetching?: boolean;
  readonly isLoading?: boolean;
  readonly logicOperators?: string[];
  readonly multiplePdfDownload?: boolean;
  readonly noColumnsButton?: boolean;
  readonly noExportButton?: boolean;
  readonly noFilterButton?: boolean;
  readonly hasRowSelection?: boolean;
  readonly onApproveDeliveryNotes?: () => void;
  readonly onBillDeliveryNotes?: () => void;
  readonly onDeclineDeliveryNotes?: () => void;
  readonly onExportCSVFromBackend?: (columnVisibilityModel: string[]) => void;
  readonly onExportExcelFromBackend?: (columnVisibilityModel: string[]) => void;
  readonly onFilterModelChange?: (
    model: GridFilterModel,
    details: GridCallbackDetails<'filter'>,
  ) => void;
  readonly customPdfExportButton?: CustomToolbarButton;
  readonly onMapDirectDeliveryNote?: () => void;
  readonly onMultiPermissionGrantEdit?: () => void;
  readonly onPdfExport?: (
    downloadOption?: string,
    doctype?: 'docx' | 'original' | 'original_enhanced',
    includeAttachments?: boolean,
  ) => void;
  readonly onRequestDeliveryNoteSignature?: () => void;
  readonly onRowClick?: (rowData: Record<string, any>) => void;
  readonly onRowRightClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  readonly onRowSelectionModelChange?: (
    selectedRows: GridRowSelectionModel,
  ) => void;
  readonly onShareDeliveryNote?: () => void;
  readonly onStateChange?: (state: GridState) => void;
  readonly pageSize?: number;
  readonly paginationMode?: GridFeatureMode;
  readonly paginationText?: string;
  readonly pinnedColumnsCookieId?: string;
  readonly productAnalyticsFeature?: string;
  readonly rows: Array<Record<string, unknown>>;
  readonly rowSelectionModel?: GridRowSelectionModel;
  readonly selectAllRowsCount?: number;
  readonly sortingMode?: GridFeatureMode;
  readonly testId?: string;
  readonly userSettingsKey?: UserSettingsKeyValues;
} & ComponentStyling;

type InitialColumnModels = {
  columnOrderModel: ColumnOrderModel;
  columnVisibilityModel: ColumnVisibilityModel;
  columnWidthModel: ColumnWidthModel;
};

/**
 * Builds the column order/visibility/width to initialize the grid with on
 * first load: the expanded preset when it needs applying, the user's stored
 * settings otherwise. Pulled out of the init effect so that effect's own
 * branching stays small.
 */
function buildInitialColumnModels({
  columnOrderModel,
  columnPreset,
  columns,
  columnVisibilityModel,
  shouldApplyColumnPreset,
  userSettings,
}: {
  columnOrderModel: ColumnOrderModel;
  columnPreset: ColumnPreset | undefined;
  columns: GridColDef[];
  columnVisibilityModel: ColumnVisibilityModel;
  shouldApplyColumnPreset: boolean;
  userSettings: Record<string, unknown>;
}): InitialColumnModels {
  if (shouldApplyColumnPreset && columnPreset) {
    return {
      ...applyColumnPreset(columns, columnPreset.orderedVisibleFields),
      columnWidthModel:
        (userSettings.columnWidthModel as ColumnWidthModel) ?? {},
    };
  }

  return {
    columnOrderModel:
      (userSettings.columnOrderModel as string[]) ?? columnOrderModel,
    columnVisibilityModel:
      (userSettings.columnVisibilityModel as ColumnVisibilityModel) ??
      columnVisibilityModel,
    columnWidthModel: (userSettings.columnWidthModel as ColumnWidthModel) ?? {},
  };
}

export const BasicTable = memo(
  ({
    checkboxSelection,
    className,
    columns,
    columnPreset,
    defaultHiddenColumns,
    defaultPinnedColumns,
    disableColumnFilter,
    disableColumnReorder,
    excelColumns,
    excelData,
    filterMode,
    filterModel,
    headerSelectionCheckboxProps,
    hideFooter,
    hideHeader,
    hideToolbar,
    initialState,
    internalExportEntityType,
    isError,
    isFetching,
    isLoading,
    logicOperators,
    multiplePdfDownload,
    noColumnsButton,
    noExportButton,
    noFilterButton,
    hasRowSelection,
    onApproveDeliveryNotes,
    onBillDeliveryNotes,
    onDeclineDeliveryNotes,
    onExportCSVFromBackend,
    onExportExcelFromBackend,
    onFilterModelChange,
    customPdfExportButton,
    onMapDirectDeliveryNote,
    onMultiPermissionGrantEdit,
    onPdfExport,
    onRequestDeliveryNoteSignature,
    onRowClick,
    onRowRightClick,
    onRowSelectionModelChange: propsOnRowSelectionModelChange,
    onShareDeliveryNote,
    onStateChange: propsOnStateChange,
    pageSize = 100,
    paginationMode,
    paginationText,
    pinnedColumnsCookieId,
    productAnalyticsFeature,
    rows,
    rowSelectionModel,
    selectAllRowsCount,
    sortingMode,
    testId,
    userSettingsKey, // Set for persisting settings (on the server) - also set as key of BasicTable for reliable resets!
    ...otherProps
  }: P) => {
    // Allow passing in an apiRef, but use the internal one if not provided
    const internalApiRef = useGridApiRef();
    const apiRef = otherProps.apiRef ?? internalApiRef;

    const { getUserSettings, persistUserSettings } = usePersistedUserSettings({
      debounceTime: 1000,
      settingsKey: userSettingsKey,
    });

    // Track initialization state to prevent race conditions
    // Use the settings key to ensure we reinitialize when switching between different tables
    const currentSettingsKey = userSettingsKey;
    const [initializedForKey, setInitializedForKey] = useState<
      string | undefined
    >(undefined);
    const [isUserSettingsLoaded, setIsUserSettingsLoaded] = useState(false);

    const isInitialized = initializedForKey === currentSettingsKey;
    const [columnOrderModel, setColumnOrderModel] = useState<ColumnOrderModel>(
      [],
    );
    const [columnVisibilityModel, setColumnVisibilityModel] =
      useState<ColumnVisibilityModel>({});
    const [columnWidthModel, setColumnWidthModel] = useState<ColumnWidthModel>(
      {},
    );
    const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState<
      GridRowId[]
    >([]);
    const [filteredSortedRows, setFilteredSortedRows] = useState<GridRowId[]>(
      [],
    );
    const isScrolledRef = useRef(false);
    const lastProcessedColumnOrderRef = useRef<string[]>([]);
    const [pinnedColumns, setPinnedColumns] = useState(
      LocalStorageService.getObjectFromLocalStorage(pinnedColumnsCookieId) ??
        defaultPinnedColumns ?? { left: [], right: [] },
    );

    const navigate = useNavigate();
    const searchParams = useSearch({ strict: false });

    const hideDefaultColumns = useCallback(
      (visibilityModel: ColumnVisibilityModel) => {
        const newColumnVisibilityModel = clone(visibilityModel);

        if (defaultHiddenColumns) {
          for (const column of defaultHiddenColumns) {
            if (newColumnVisibilityModel[column] === undefined) {
              newColumnVisibilityModel[column] = false;
            }
          }
        }

        return newColumnVisibilityModel;
      },
      [defaultHiddenColumns],
    );

    // If we have a scrollY in the search params, scroll to it after the component mounts
    useLayoutEffect(() => {
      const { scrollY } = searchParams;
      const { page } = searchParams;
      let scrollTimeout: NodeJS.Timeout | undefined;
      let pageTimeout: NodeJS.Timeout | undefined;
      if (!isScrolledRef.current && scrollY && apiRef.current.scroll) {
        scrollTimeout = setTimeout(() => {
          if (rows.length > 0) {
            isScrolledRef.current = true;
            apiRef.current.scroll({ top: Number(scrollY) });
          }
        }, 100);
      }

      if (apiRef.current.setState) {
        apiRef.current.setState((previous) => ({
          ...previous,
          customMetadata: { internalExportEntityType },
        }));
      }

      if (apiRef.current.setPage && page) {
        pageTimeout = setTimeout(() => {
          apiRef.current.setPage(Number(page));
        }, 100);
        // With the current implementation of this table, we have to remove the page once it's set otherwise we get stuck on the same page
        // If the page would have been controlled by the url params to begin with, we would not have had this issue
        void navigate({
          search(prev: Record<string, unknown>) {
            const { page, ...rest } = prev;
            return rest;
          },
          replace: true,
        });
      }

      return () => {
        if (pageTimeout) {
          clearTimeout(pageTimeout);
        }

        if (scrollTimeout) {
          clearTimeout(scrollTimeout);
        }
      };
    }, [apiRef, rows, internalExportEntityType, searchParams, navigate]);

    // Initialize settings only once and track when user settings are loaded
    useLayoutEffect(() => {
      if (isInitialized) {
        return;
      }

      const userSettings = getUserSettings() ?? {};
      const shouldApplyColumnPreset = getShouldApplyColumnPreset(
        userSettings,
        columnPreset,
      );

      // Expanding the preset needs the real column set; wait for it.
      if (shouldApplyColumnPreset && columns.length === 0) {
        return;
      }

      const models = buildInitialColumnModels({
        columnOrderModel,
        columnPreset,
        columns,
        columnVisibilityModel,
        shouldApplyColumnPreset,
        userSettings,
      });

      models.columnVisibilityModel = hideDefaultColumns(
        models.columnVisibilityModel,
      );

      setColumnOrderModel(models.columnOrderModel);
      setColumnVisibilityModel(models.columnVisibilityModel);
      setColumnWidthModel(models.columnWidthModel);

      if (shouldApplyColumnPreset && columnPreset) {
        // Record which preset was applied, together with the preset models,
        // so the reset happens exactly once. After this persist triggers a
        // refetch, the marker (plus the deep-equal no-op in
        // persistUserSettings) prevents a write loop.
        persistUserSettings({
          ...userSettings,
          appliedColumnPreset: {
            id: columnPreset.id,
            version: columnPreset.version,
          },
          columnOrderModel: models.columnOrderModel,
          columnVisibilityModel: models.columnVisibilityModel,
          columnWidthModel: models.columnWidthModel,
        });
      }

      // Always set to true after attempting to load, regardless of whether settings exist
      setIsUserSettingsLoaded(true);
      setInitializedForKey(currentSettingsKey);
    }, [
      userSettingsKey,
      currentSettingsKey,
      isInitialized,
      columns,
      columnPreset,
    ]);

    // Reset initialization state when settings key changes
    useEffect(() => {
      if (initializedForKey && initializedForKey !== currentSettingsKey) {
        setInitializedForKey(undefined);
        setIsUserSettingsLoaded(false);
        // Reset the column order tracking ref for the new settings key
        lastProcessedColumnOrderRef.current = [];
      }
    }, [currentSettingsKey, initializedForKey]);

    useEffect(() => {
      setColumnVisibilityModel((previousModel) =>
        hideDefaultColumns(previousModel),
      );
    }, [defaultHiddenColumns, hideDefaultColumns]);

    const orderedAndSizedColumns = useMemo(() => {
      const orderedColumns = orderColumns(
        columns,
        columnOrderModel,
        disableColumnReorder,
      );

      return sizeColumns(orderedColumns, columnWidthModel);
    }, [columns, columnOrderModel, columnWidthModel, disableColumnReorder]);

    const hiddenHeaderProps = hideHeader
      ? {
          columnHeaderHeight: 0,
          sx: {
            '& .MuiDataGrid-columnHeaders': { display: 'none !important' },
            '& .MuiDataGrid-toolbarContainer': {
              display: 'none !important',
              height: '0 !important',
              padding: '0 !important',
              visibility: 'hidden !important',
            },
          },
        }
      : null;

    const getPinnedColumns = useCallback(() => {
      if (pinnedColumns === null) {
        return (
          defaultPinnedColumns ?? {
            left: [],
            right: [],
          }
        );
      }

      return pinnedColumns;
    }, [pinnedColumns, defaultPinnedColumns]);

    const memoizedExcelData = useMemo(() => {
      if (!excelData?.length) {
        return [];
      }

      if (!filteredSortedRows?.length) {
        return excelData;
      }

      return sortByKeyValues(excelData, filteredSortedRows, 'id');
    }, [excelData, filteredSortedRows]);

    const toolbarProps = useMemo(
      () => ({
        apiRef,
        customPdfExportButton,
        excelColumns,
        excelData: memoizedExcelData,
        hasRowSelection,
        multiplePdfDownload,
        noColumnsButton,
        noExportButton,
        noFilterButton,
        onApproveDeliveryNotes,
        onBillDeliveryNotes,
        onDeclineDeliveryNotes,
        onExportCSVFromBackend,
        onExportExcelFromBackend,
        onMapDirectDeliveryNote,
        onMultiPermissionGrantEdit,
        onPdfExport,
        onRequestDeliveryNoteSignature,
        onShareDeliveryNote,
        productAnalyticsFeature,
        rowsCount: rows?.length ?? 0,
      }),
      [
        apiRef,
        excelColumns,
        memoizedExcelData,
        multiplePdfDownload,
        noColumnsButton,
        noExportButton,
        noFilterButton,
        hasRowSelection,
        onApproveDeliveryNotes,
        onBillDeliveryNotes,
        onDeclineDeliveryNotes,
        onExportCSVFromBackend,
        onExportExcelFromBackend,
        customPdfExportButton,
        onMapDirectDeliveryNote,
        onMultiPermissionGrantEdit,
        onPdfExport,
        onRequestDeliveryNoteSignature,
        onShareDeliveryNote,
        productAnalyticsFeature,
        rows?.length,
      ],
    );

    const onColumnVisibilityModelChange = useCallback(
      (newModel: ColumnVisibilityModel) => {
        log.info({
          message: 'Change column visibility',
          data: {
            from: columnVisibilityModel,
            to: newModel,
          },
          breadcrumb: {
            category: LOG.BREADCRUMB.FILTER_CHANGE.CATEGORY,
            event_id: LOG.BREADCRUMB.FILTER_CHANGE.KEY,
            message: LOG.BREADCRUMB.FILTER_CHANGE.DEFAULT_MESSAGE,
            type: LOG.BREADCRUMB.FILTER_CHANGE.TYPE,
          },
        });
        log.productAnalyticsEvent(
          'Change column visibility',
          LOG.FEATURE.BASIC_TABLE,
        );

        setColumnVisibilityModel(newModel);
        // Spread the stored settings so keys not managed here
        // (e.g. appliedColumnPreset) survive the full-object write.
        persistUserSettings({
          ...getUserSettings(),
          columnOrderModel,
          columnVisibilityModel: newModel,
          columnWidthModel,
        });
      },
      [
        columnOrderModel,
        columnVisibilityModel,
        columnWidthModel,
        getUserSettings,
        persistUserSettings,
      ],
    );

    const onColumnWidthChange = useCallback(
      (event: GridColumnResizeParams) => {
        if (!event.colDef.width) {
          return;
        }

        const newColumnWidthModel = clone(columnWidthModel);
        newColumnWidthModel[event.colDef.field] = event.colDef.width;

        log.info({
          message: 'Change column width',
          data: {
            from: columnWidthModel,
            to: newColumnWidthModel,
          },
          breadcrumb: {
            category: LOG.BREADCRUMB.FILTER_CHANGE.CATEGORY,
            event_id: LOG.BREADCRUMB.FILTER_CHANGE.KEY,
            message: LOG.BREADCRUMB.FILTER_CHANGE.DEFAULT_MESSAGE,
            type: LOG.BREADCRUMB.FILTER_CHANGE.TYPE,
          },
        });
        log.productAnalyticsEvent(
          'Change column width',
          LOG.FEATURE.BASIC_TABLE,
        );

        setColumnWidthModel(newColumnWidthModel);
        persistUserSettings({
          ...getUserSettings(),
          columnOrderModel,
          columnVisibilityModel,
          columnWidthModel: newColumnWidthModel,
        });
      },
      [
        columnOrderModel,
        columnVisibilityModel,
        columnWidthModel,
        getUserSettings,
        persistUserSettings,
      ],
    );

    const onColumnOrderChange = useCallback(
      async (newColumnOrderModel: ColumnOrderModel) => {
        // Don't persist column order if we don't have all columns yet or not fully initialized
        if (!columns?.length || !isInitialized || !isUserSettingsLoaded) {
          return;
        }

        const storedColumnOrderModel = columnOrderModel;

        // Validate that new order contains all current columns
        const currentFields = new Set(columns.map(({ field }) => field));
        const newOrderFields = new Set(newColumnOrderModel);
        const isValidOrder = [...currentFields].every((field) =>
          newOrderFields.has(field),
        );

        if (!isValidOrder) {
          return;
        }

        // Detect changes in content and order
        const hasChanges =
          JSON.stringify(storedColumnOrderModel) !==
          JSON.stringify(newColumnOrderModel);

        if (!hasChanges) {
          return;
        }

        setColumnOrderModel(newColumnOrderModel);

        // Don't persist during initial loading phase or if no previous state exists
        if (!storedColumnOrderModel?.length) {
          return;
        }

        persistUserSettings({
          ...getUserSettings(),
          columnOrderModel: newColumnOrderModel,
          columnVisibilityModel,
          columnWidthModel,
        });

        log.info({
          message: 'Change column order',
          data: {
            from: storedColumnOrderModel,
            to: newColumnOrderModel,
          },
          breadcrumb: {
            category: LOG.BREADCRUMB.FILTER_CHANGE.CATEGORY,
            event_id: LOG.BREADCRUMB.FILTER_CHANGE.KEY,
            message: LOG.BREADCRUMB.FILTER_CHANGE.DEFAULT_MESSAGE,
            type: LOG.BREADCRUMB.FILTER_CHANGE.TYPE,
          },
        });
        log.productAnalyticsEvent(
          'Change column order',
          LOG.FEATURE.BASIC_TABLE,
        );
      },
      [
        columns,
        columnOrderModel,
        columnVisibilityModel,
        columnWidthModel,
        getUserSettings,
        persistUserSettings,
        isInitialized,
        isUserSettingsLoaded,
      ],
    );

    const onStateChange = useCallback(
      (state: GridState) => {
        // Only update column order if it actually changed and is different from last processed
        const newColumnOrder = state.columns.orderedFields;
        const newColumnOrderString = JSON.stringify(newColumnOrder);
        const lastProcessedString = JSON.stringify(
          lastProcessedColumnOrderRef.current,
        );

        if (newColumnOrderString !== lastProcessedString) {
          lastProcessedColumnOrderRef.current = newColumnOrder;
          onColumnOrderChange(newColumnOrder);
        }

        if (propsOnStateChange) {
          propsOnStateChange(state);
        }

        if (!excelData) {
          return;
        }

        // on unMount of the datagrid, apiRef.current is set to null {new refs `current` is empty not null}
        if (!apiRef?.current) {
          return;
        }

        const newFilteredSortedRows = unique(
          gridFilteredSortedRowIdsSelector(state, apiRef.current.instanceId),
        );

        if (
          JSON.stringify(filteredSortedRows) ===
          JSON.stringify(newFilteredSortedRows)
        ) {
          return;
        }

        setFilteredSortedRows(newFilteredSortedRows);
      },
      [
        onColumnOrderChange,
        propsOnStateChange,
        excelData,
        filteredSortedRows,
        apiRef,
      ],
    );

    const onRowSelectionModelChange = useCallback(
      (event: GridRowSelectionModel) => propsOnRowSelectionModelChange?.(event),
      [propsOnRowSelectionModelChange],
    );

    const onPinnedColumnsChange = useCallback(
      (event: GridPinnedColumnFields) => {
        log.info({
          message: 'Change pinned columns',
          data: {
            from: pinnedColumns,
            to: event,
          },
          breadcrumb: {
            category: LOG.BREADCRUMB.FILTER_CHANGE.CATEGORY,
            event_id: LOG.BREADCRUMB.FILTER_CHANGE.KEY,
            message: LOG.BREADCRUMB.FILTER_CHANGE.DEFAULT_MESSAGE,
            type: LOG.BREADCRUMB.FILTER_CHANGE.TYPE,
          },
        });
        log.productAnalyticsEvent(
          'Change pinned columns',
          LOG.FEATURE.BASIC_TABLE,
        );

        setPinnedColumns(event);

        LocalStorageService.setObjectAsLocalStorage(
          pinnedColumnsCookieId,
          event,
        );
      },
      [pinnedColumns, pinnedColumnsCookieId],
    );

    // Created an adapter for onRowClick to save the scroll position to the search params before running the passed in onRowClick
    const onRowClickHandler = useCallback(
      (event: GridRowParams) => {
        if (!onRowClick) {
          return;
        }

        const scrollPosition = apiRef.current.getScrollPosition();
        const page =
          apiRef?.current?.state?.pagination?.paginationModel?.page ?? 0;
        void navigate({
          search: (prev) => ({
            ...prev,
            scrollY: String(scrollPosition.top),
            page: String(page),
          }),
          replace: true,
        });

        onRowClick?.(event);
      },
      [onRowClick, apiRef, navigate],
    );

    const slots = useMemo(
      () => ({
        footer: hideFooter ? () => null : Footer,
        loadingOverlay: () => <LoadingState />,
        noRowsOverlay: () => <NoRowsOverlay isError={isError} />,
        pagination: Pagination,
        toolbar: hideToolbar ? () => null : GridToolbar,
      }),
      // apiRef?.current is used to trigger a re-render of the toolbar when the apiRef changes, TS shows this is unnecessary but it is
      [hideFooter, hideToolbar, isError, apiRef?.current],
    );

    const slotProps = useMemo(
      () => ({
        ...(logicOperators && { filterPanel: { logicOperators } }),
        footer: {
          isFetching: isFetching ?? false,
          paginationText,
          selectedRowCount: selectAllRowsCount,
        },
        pagination: {
          'data-testid': ComponentTestIds.TABLE.PAGINATION,
          isFetching: isFetching ?? false,
          paginationText,
        },
        row: {
          onContextMenu: onRowRightClick,
          style: {
            cursor: onRowRightClick
              ? 'context-menu'
              : onRowClick
                ? 'pointer'
                : 'auto',
          },
        },
        toolbar: toolbarProps,
      }),
      [
        isFetching,
        logicOperators,
        onRowClick,
        onRowRightClick,
        paginationText,
        selectAllRowsCount,
        toolbarProps,
      ],
    );

    // Ensure detailPanelExpandedRowIds is an array of non-null values (null breaks the DataGridPro component)
    const filteredDetailPanelExpandedRowIds = detailPanelExpandedRowIds.filter(
      (id) => id !== null,
    );

    return (
      <DataGridPro
        apiRef={apiRef}
        checkboxSelection={checkboxSelection}
        className={className}
        columnVisibilityModel={columnVisibilityModel}
        columns={orderedAndSizedColumns}
        data-testid={testId}
        detailPanelExpandedRowIds={filteredDetailPanelExpandedRowIds}
        disableColumnFilter={disableColumnFilter}
        disableColumnSorting={isFetching}
        filterModel={filterModel}
        headerSelectionCheckboxProps={headerSelectionCheckboxProps}
        initialState={{
          pinnedColumns: getPinnedColumns(),
          ...initialState,
        }}
        loading={isLoading}
        paginationMode={paginationMode}
        rowHeight={DatagridUtils.ROW_HEIGHT.THIN}
        rowSelectionModel={rowSelectionModel}
        rows={rows}
        slotProps={slotProps}
        slots={slots}
        sortingMode={sortingMode}
        disableRowSelectionOnClick
        pagination
        onColumnVisibilityModelChange={onColumnVisibilityModelChange}
        onColumnWidthChange={onColumnWidthChange}
        onDetailPanelExpandedRowIdsChange={setDetailPanelExpandedRowIds}
        onFilterModelChange={onFilterModelChange}
        onPinnedColumnsChange={onPinnedColumnsChange}
        onRowClick={onRowClickHandler}
        onRowSelectionModelChange={onRowSelectionModelChange}
        onStateChange={onStateChange}
        {...hiddenHeaderProps}
        {...otherProps}
      />
    );
  },
);

BasicTable.displayName = 'BasicTable';
